import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useClassName } from '@travpro/hooks';
import i18n from 'i18n-react';
import { renderToString } from 'react-dom/server';

import PointsDisplay from 'components/PointsDisplay';
import SharingPopup from 'components/SharingPopup';

import { capitalize } from 'helpers/helpers';

const params = new URLSearchParams(window.location.search);
let mobile = false;
if (params.get('mobile')) {
  mobile = true;
}
/**
 * @typedef {object} BadgeDisplayProps
 * @property {string[]} tierBadges An array of badge image urls, one for each tier.
 * @property {number} tierIndex The index of the current tier.
 * @property {object[]} tiers An array of tier objects, each containing a title and a points value.
 * @property {number} totalPoints The total number of points the user has.
 * @property {string} title The title of the badge display.
 * @property {string} layout Layout style defines the look or shape of the badge display. A horizontal bar, a vertical
 *   pilar or a compact box.
 * @property {string} position Position defines the location of the badge display, either a regular inline component or
 *   a fixed element on the left or right side of the screen.
 * @property {boolean} float Float defines whether the badge display should float/stick on the screen or not. Only
 *   applies when position is set to left or right.
 */

/**
 * A display component used to show the user's current progress.
 *
 * @param {BadgeDisplayProps} props The badge display props.
 * @returns {JSX.Element} A component that displays a badge with the user's current status and points (implemnted with
 *   PointsDisplay)
 */
const BadgeDisplay = ({
  tierBadges,
  tierIndex = null,
  tiers,
  totalPoints,
  badgeSubtitleOverride = '',
  pointsBarSubtitle = '',
  layout = 'box',
  position = 'inline',
  hideBadge = false,
}) => {
  // Bail out if we don't have the required props (yet)
  const requiredProps = [tiers, totalPoints];

  const c = useClassName('badge-display');

  if (requiredProps.some((prop) => prop === undefined || prop === null)) {
    return null;
  }

  const layouts = ['box', 'bar', 'pillar', 'bigBar', 'transparantBar', 'longBar'];
  const barShapedLayouts = ['bar', 'bigBar', 'transparantBar', 'longBar'];

  return (
    <div
      {...c({
        [`${layout}`]: layouts.includes(layout) && layout !== 'box',
        box: !layouts.includes(layout) || layout === 'box',
        static: ['left', 'right'].includes(position),
        [`${position}`]: ['left', 'right', 'inline'].includes(position),
        inline: !['left', 'right'].includes(position),
      })}
    >
      <div
        {...c('inner', {
          horizontal: !barShapedLayouts.includes(layout),
          vertical: barShapedLayouts.includes(layout),
        })}
      >
        <div {...c('subcontainer')}>
          {!hideBadge && !mobile && (
            <a
              href={Object.values(tierBadges)[tierIndex]}
              data-fancybox={Object.values(tierBadges)[tierIndex]}
              data-options={JSON.stringify({
                buttons: ['download', 'share', 'close'],
                share: {
                  tpl: renderToString(<SharingPopup url={Object.values(tierBadges)[tierIndex]} downloadable={false}/>),
                },
                btnTpl: {
                  download: renderToString(
                    <a
                      href={`https://stack.travpromobile.com/download.php?url=${Object.values(tierBadges)[tierIndex]}&name=${tiers[tierIndex].title}`}
                    >
                      <button 
                        type='button' 
                        data-fancybox-download 
                        className='fancybox-button fancybox-button--download'
                        title='Download'
                      >
                        <FontAwesomeIcon icon={['fas', "download"]} />
                      </button>
                    </a>
                  )
                },
              })}
            >
              <img {...c('badge')} src={Object.values(tierBadges)[tierIndex]} alt="Logo" />
            </a>
          )}
          {!hideBadge && mobile && (
            <a onClick={() => window.ReactNativeWebView.postMessage(`webview:${Object.values(tierBadges)[tierIndex]}`)}>
              <img {...c('badge')} src={Object.values(tierBadges)[tierIndex]} alt="Logo" />
            </a>
          )}
          {tierIndex !== null && badgeSubtitleOverride !== '' && (
            <span {...c('wrapper-span', 'badge-subtitle')}>
              {badgeSubtitleOverride || `${i18n.translate('trainingScreens.status')} ${tiers[tierIndex].title}`}
            </span>
          )}
          {/* <ReactTooltip /> */}
        </div>
        {/* <div
          {...c('divider', {
            horizontal: !['bar', 'bigBar', 'transparantBar', 'longBar'].includes(layout),
            vertical: ['bar', 'bigBar'].includes(layout),
          })}
        /> */}
        <div {...c('subcontainer')}>
          <span {...c('wrapper-span', 'pointsbar')}>
            <PointsDisplay
              orientation={['pillar'].includes(layout) ? 'vertical' : 'horizontal'}
              tiers={tiers.map(({ points: p }) => p)}
              labels={tiers.map(({ title }) => capitalize(title))}
              // minSize={'40rem'}
              points={totalPoints}
              segmented
            />
          </span>
          <span {...c('wrapper-span', 'pointsbar-subtitle')}>{pointsBarSubtitle}</span>
        </div>
      </div>
    </div>
  );
};

export default BadgeDisplay;

BadgeDisplay.propTypes = {
  tierBadges: PropTypes.arrayOf(PropTypes.string).isRequired,
  tierIndex: PropTypes.number,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      points: PropTypes.number,
    }),
  ).isRequired,
  totalPoints: PropTypes.number.isRequired,
  badgeSubtitleOverride: PropTypes.string,
  pointsBarSubtitle: PropTypes.string,
  layout: PropTypes.oneOf(['box', 'bar', 'pillar', 'bigBar', 'transparantBar', 'longBar']),
  position: PropTypes.oneOf(['inline', 'left', 'right']),
  hideBadge: PropTypes.bool,
};
